import React from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Truck } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useApprox } from '../product/Money';
import { useDeliveryFacts } from '../../contexts/StoreFactsContext';
import { deliveryTimeText, freeDeliveryText } from '../../utils/deliveryZones';
import { useVariant } from '../../utils/experiments';

/**
 * Two-cell benefit strip (SHEIN's "Free Shipping | Top Brands"). The
 * delivery line comes from GET /settings/shipping/zones only: the free
 * threshold when every paid UAE zone has one, else the delivery time, else
 * a line without numbers.
 *
 * A/B testing (API_CONTRACT §6n): payload key `home_promise_band` (true =
 * shown, today's UI). Read once at mount, so the band never pops in or out.
 */
const HomeBenefits = () => {
  const { t } = useLanguage();
  const facts = useDeliveryFacts();
  const free = freeDeliveryText(facts, useApprox());
  const time = deliveryTimeText(facts);
  const shown = useVariant('home_promise_band', true);
  if (shown === false) return null;

  let deliveryTitle = { en: 'UAE delivery', ar: 'توصيل داخل الإمارات' };
  let deliveryLine = { en: 'Fees and timing shown at checkout', ar: 'الرسوم والمدة تظهر عند الدفع' };
  if (facts?.allFree) {
    deliveryTitle = { en: 'Free delivery', ar: 'توصيل مجاني' };
    deliveryLine = { en: 'On every order across the UAE', ar: 'على كل الطلبات داخل الإمارات' };
  } else if (free && facts?.freeOver != null) {
    const amount = Number(facts.freeOver).toLocaleString('en-US', { maximumFractionDigits: 2 });
    deliveryTitle = { en: 'Free delivery', ar: 'توصيل مجاني' };
    deliveryLine = { en: `On UAE orders over ${amount} AED`, ar: `للطلبات فوق ${amount} درهم داخل الإمارات` };
  } else if (time) {
    deliveryLine = time;
  }

  const cell = 'flex min-h-[60px] min-w-0 flex-1 items-center gap-2.5 px-3 py-2 transition-colors hover:bg-black/[0.03] md:px-5';
  return (
    <section className="luxury-stage-wide pt-3 lg:pt-5" aria-label={t({ en: 'Why shop with us', ar: 'لماذا تتسوق معنا' })} data-testid="home-benefits">
      <div className="flex overflow-hidden rounded-2xl bg-tint-butter">
        <Link to="/shipping-policy" className={cell}>
          <Truck size={22} strokeWidth={2} aria-hidden="true" className="shrink-0 text-crimson-ink" />
          <span className="min-w-0">
            <span className="block text-[14px] font-bold leading-tight text-foreground md:text-[15px]">{t(deliveryTitle)}</span>
            <span className="mt-0.5 block text-[12px] leading-snug text-stone md:text-[13px]">{t(deliveryLine)}</span>
          </span>
        </Link>
        <span aria-hidden="true" className="my-3 w-px shrink-0 bg-foreground/15" />
        <Link to="/brands" className={cell}>
          <BadgeCheck size={22} strokeWidth={2} aria-hidden="true" className="shrink-0 text-crimson-ink" />
          <span className="min-w-0">
            <span className="block text-[14px] font-bold leading-tight text-foreground md:text-[15px]">{t({ en: 'Authentic designers', ar: 'علامات أصلية' })}</span>
            <span className="mt-0.5 block text-[12px] leading-snug text-stone md:text-[13px]">{t({ en: 'From houses we trust', ar: 'من دور نثق بها' })}</span>
          </span>
        </Link>
      </div>
    </section>
  );
};

export default HomeBenefits;
